import { Button, Divider, Grid } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import {
  getUserById,
  getUsersWithFilters,
  MIXPANEL_EVENTS,
  PermissionsEnum,
  Property,
  removeUser as removeUserMutation,
} from 'vendor-marketplace-shared-library';
import UserPropertiesModule from './components/UserPropertiesModule';
import UserDetailsHeader from './components/UserDetailsHeader';
import UserWorkOrdersModule from './components/UserWorkOrdersModule';
import React, { useEffect, useState } from 'react';
import useAuth from '../../hooks/useAuth';
import { REMOVE_USER_FROM_ORGANIZATION } from '../../error-messages';
import useAlert from '../../hooks/useAlert';
import Modal from '../../components/common/Modal';
import useMixpanel from '../../hooks/useMixpanel';

export default function UserDetailsPage() {
  const { validateUserPermissions } = useAuth();
  const { setAlert } = useAlert();
  const navigate = useNavigate();
  const { mixpanelTrack } = useMixpanel();
  const { id } = useParams<{ id: string }>();
  const [openRemovalModal, setOpenRemovalModal] = useState(false);
  const [selectedPropertyIds, setSelectedPropertyIds] = useState<string[]>([]);

  const { data: userData, refetch: refetchUserData } = useQuery(getUserById, {
    variables: { id },
  });

  const [removeUser] = useMutation(removeUserMutation, {
    refetchQueries: [getUsersWithFilters],
    onError: () => {
      setAlert({ severity: 'error', message: REMOVE_USER_FROM_ORGANIZATION });
    },
    onCompleted: () => {
      navigate('/users');
      mixpanelTrack(MIXPANEL_EVENTS.ORGANIZATION_SETTINGS_USER_REMOVED_FROM_ORGANIZATION);
    },
  });

  const onRemoveUser = async () => {
    await removeUser({
      variables: { id },
    });
  };

  useEffect(() => {
    setSelectedPropertyIds(
      userData?.getUserById.properties.map((property: Property) => property.id) ?? [],
    );
  }, [userData?.getUserById]);

  return (
    <Grid container rowGap={4} columnGap={2}>
      <Grid item xs={12}>
        <UserDetailsHeader user={userData?.getUserById ?? {}} />
      </Grid>
      <Grid item xs={12}>
        <Divider />
      </Grid>
      <Grid item xs={12} md={5}>
        <UserPropertiesModule
          user={userData?.getUserById ?? {}}
          userProperties={userData?.getUserById.properties ?? []}
          selectedPropertyIds={selectedPropertyIds}
          setSelectedPropertyIds={setSelectedPropertyIds}
          refetchUserData={refetchUserData}
        />
      </Grid>
      <Grid item xs={12} md={5}>
        <UserWorkOrdersModule userId={userData?.getUserById?.id} />
      </Grid>
      {validateUserPermissions([PermissionsEnum.USERS_DELETE]) && (
        <Grid item>
          <Button onClick={() => setOpenRemovalModal(true)} color={'error'}>
            Remove from organization
          </Button>
        </Grid>
      )}
      {openRemovalModal && (
        <Modal title={'Remove user from org'} open={openRemovalModal} setOpen={setOpenRemovalModal}>
          <Grid container rowGap={2} direction={'column'} paddingY={2}>
            <Grid item>Are you sure you want to remove this user from the organization?</Grid>
            <Grid item>
              <Button
                onClick={() => {
                  setOpenRemovalModal(false);
                  onRemoveUser();
                  navigate('/users');
                }}
                color={'error'}
              >
                Remove
              </Button>
            </Grid>
          </Grid>
        </Modal>
      )}
    </Grid>
  );
}
